import { ActionBar, Button, Modal } from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import { useModal } from 'common/hooks/useModalGrapes';
import { useTranslation } from 'common/hooks/useTranslation';

import { CustomFieldValuesListContainer } from '../../containers/CustomFieldValuesListContainer';
import { useDeleteCustomFieldValuesMutation } from '../../hooks/useDeleteCustomFieldValues';
import { type CustomField } from '../../models/customField';
import { BooleanCustomField } from '../fields/BooleanCustomField';

type Props = {
  customFields: CustomField[];
  groups: { id: string; name: string }[];
};

export const CustomFields = ({ customFields, groups }: Props) => {
  const { t } = useTranslation('global');
  const [selectedCustomFieldValueIds, setSelectedCustomFieldValueIds] =
    useState<Record<string, string[]>>({});
  const deleteCustomFieldValues = useDeleteCustomFieldValuesMutation();

  const handleDeleteCustomFieldValues = async () => {
    for (const customFieldId of Object.keys(selectedCustomFieldValueIds)) {
      await deleteCustomFieldValues({
        customFieldId,
        customFieldValueIds: selectedCustomFieldValueIds[customFieldId],
      });
    }
    setSelectedCustomFieldValueIds({});
  };

  const flattenedSelectedValueIds = Object.values(
    selectedCustomFieldValueIds,
  ).flat();

  const [deleteConfirmationModal, showDeleteConfirmationModal] = useModal(
    ({ isOpen, onClose }) => {
      const count = flattenedSelectedValueIds.length;
      const fieldBreakdownItems = Object.entries(selectedCustomFieldValueIds)
        .map(([customFieldId, valueIds]) => {
          const field = customFields.find((f) => f.id === customFieldId);
          if (!field || valueIds.length === 0) {
            return null;
          }
          const valueCount = valueIds.length;
          return {
            key: customFieldId,
            label: t('customFields.settings.modals.deletion.fieldBreakdown', {
              count: valueCount,
              fieldName: field.name,
            }),
          };
        })
        .filter(
          (
            item,
          ): item is {
            key: string;
            label: string;
          } => Boolean(item),
        );

      return (
        <Modal
          isOpen={isOpen}
          iconName="cross"
          iconVariant="alert"
          title={t('customFields.settings.modals.deletion.bulkTitle', {
            count,
          })}
          actions={[
            <Button
              variant="secondaryNeutral"
              text={t('customFields.settings.modals.deletion.cancel')}
              onClick={() => onClose()}
              key="secondary"
            />,
            <Button
              variant="primaryAlert"
              text={t('customFields.settings.modals.deletion.submit')}
              onClick={async () => {
                await handleDeleteCustomFieldValues();
                onClose();
              }}
              key="primary"
            />,
          ]}
        >
          <div className="flex flex-col gap-12">
            <p>
              {t('customFields.settings.modals.deletion.bulkDescription', {
                count,
              })}
            </p>
            {fieldBreakdownItems.length > 0 && (
              <div className="flex flex-col gap-4">
                {fieldBreakdownItems.map((item) => (
                  <span key={item.key}>{item.label}</span>
                ))}
              </div>
            )}
          </div>
        </Modal>
      );
    },
  );

  return (
    <div className="w-full max-w-lg self-center">
      {customFields.map((field) => {
        if (field.type === 'list') {
          return (
            <CustomFieldValuesListContainer
              key={field.id}
              customField={field}
              selectedValueIds={selectedCustomFieldValueIds[field.id] ?? []}
              onSelectedValuesChange={(selectedValueIds, customFieldId) => {
                setSelectedCustomFieldValueIds((previous) => {
                  const updated = { ...previous };
                  updated[customFieldId] = selectedValueIds;
                  return updated;
                });
              }}
            />
          );
        }
        if (field.type === 'boolean') {
          return (
            <BooleanCustomField
              key={field.id}
              customField={field}
              groups={groups}
            />
          );
        }
        return null;
      })}

      {flattenedSelectedValueIds.length > 0 && (
        <div className="sticky bottom-16 left-0 flex justify-center">
          <ActionBar
            actions={[
              {
                text: t('misc.delete'),
                onClick: () => showDeleteConfirmationModal(),
              },
            ]}
          >
            {t('misc.nSelected', {
              count: flattenedSelectedValueIds.length,
            })}
          </ActionBar>
        </div>
      )}
      {deleteConfirmationModal}
    </div>
  );
};
